/**
 * main.jsx — the entry point. This is the first of your code that runs.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

const container = document.getElementById('root');

if (!container) {
  throw new Error('No #root element — check index.html.');
}

/**
 * StrictMode double-invokes effects in development only.
 *
 * That is deliberate on React's part: it surfaces subscriptions that are set up
 * but never cleaned up. In an app built on Firestore listeners, a missed
 * cleanup is a memory leak and a duplicated write, so this is worth the noise.
 * It does not run in the production build.
 */
createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>
);

/**
 * Last-resort error logging.
 *
 * The ErrorBoundary in App.jsx catches errors thrown during render. These two
 * handlers catch the ones it cannot see: failures inside event handlers, and
 * promises that reject with nobody awaiting them. Without them, those fail
 * completely silently and you never learn they happened.
 *
 * Wire your error reporter (Sentry, or similar) here. Scrub the message before
 * sending: React errors can contain rendered props, which in this app may
 * include the text of a private message.
 */
window.addEventListener('error', (event) => {
  console.error('Uncaught error', event.error ?? event.message);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection', event.reason);
});
